// Component wrapper for kendo-react Checkbox that provides normalized events, label, and common defaults
import * as React from "react";
import {
  Checkbox,
  CheckboxChangeEvent,
  CheckboxProps,
} from "@progress/kendo-react-inputs";
import { StackLayout } from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { CPText } from "shared/components/base";

export interface CPCheckboxChangeEvent {
  type: "CPCheckboxChangeEvent";
  name: string;
  value: boolean;
  syntheticEvent: React.SyntheticEvent<HTMLElement, Event>;
}

export interface CPCheckboxProps extends Omit<
  CheckboxProps,
  "name" | "label" | "onChange"
> {
  /** Unique field name included in every change event. */
  name: string;
  /** Text label rendered next to the checkbox. */
  label?: string;
  /** Helper text shown below the checkbox when there is no error. */
  helpText?: string;
  /** Error message displayed in red below the checkbox. */
  errorMsg?: string;
  /** Callback fired when the checked state changes. */
  onChange?: (event: CPCheckboxChangeEvent) => void;
  /** When provided, the help icon becomes a clickable button that fires this callback instead of showing a tooltip. */
  onHelpClick?: () => void;
}

/**
 * Wrapper around the Kendo `Checkbox` with normalised change events, floating label, and
 * inline help/error text.
 *
 * @example
 * ```tsx
 * // Basic checkbox
 * <CPCheckbox
 *   name="isActive"
 *   label="Active"
 *   checked={form.isActive}
 *   onChange={handleChange}
 * />
 *
 * // With error message
 * <CPCheckbox
 *   name="acceptTerms"
 *   label="I accept the terms and conditions"
 *   checked={form.acceptTerms}
 *   errorMsg={errors.acceptTerms}
 *   onChange={handleChange}
 * />
 * ```
 */
const CPCheckbox: React.FC<CPCheckboxProps> = (props) => {
  const { name, label, helpText, errorMsg, onChange, onHelpClick, ...rest } =
    props;

  const handleChange = (event: CheckboxChangeEvent) => {
    if (onChange) {
      onChange({
        type: "CPCheckboxChangeEvent",
        name,
        value: event.value,
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  const helpIconStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "1px solid #0066cc",
    fontSize: 10,
    fontWeight: "bold",
    color: "#0066cc",
    flexShrink: 0,
  };

  const renderHelpIcon = () => {
    if (onHelpClick) {
      return (
        <button
          type="button"
          onClick={onHelpClick}
          aria-label="Field help"
          style={{
            ...helpIconStyle,
            cursor: "pointer",
            background: "none",
            padding: 0,
          }}
        >
          ?
        </button>
      );
    }
    if (helpText) {
      return (
        <Tooltip anchorElement="target" position="top">
          <span
            title={helpText}
            style={{ ...helpIconStyle, cursor: "help", userSelect: "none" }}
          >
            ?
          </span>
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <StackLayout orientation="vertical">
      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Checkbox
          {...rest}
          name={name}
          label={label}
          size={props.size ?? "small"}
          onChange={handleChange}
        />
        {renderHelpIcon()}
      </div>
      {errorMsg && (
        <CPText textStyle="caption" color="error">
          {errorMsg}
        </CPText>
      )}
    </StackLayout>
  );
};

export default CPCheckbox;
